import React from 'react';
import { LucideIcon } from 'lucide-react';

interface ActionCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  onClick: () => void;
}

const ActionCard: React.FC<ActionCardProps> = ({ icon: Icon, title, description, onClick }) => {
  const [ripples, setRipples] = React.useState<Array<{ x: number; y: number; id: number }>>([]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const newRipple = { x, y, id: Date.now() };

    setRipples((prev) => [...prev, newRipple]);

    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
    }, 600);

    onClick();
  };

  return (
    <button
      onClick={handleClick}
      className="group relative bg-gradient-to-br from-white to-[#05E0D1]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 text-left transition-all duration-300 hover:border-[#05E0D1] hover:shadow-2xl hover:shadow-[#05E0D1]/30 hover:-translate-y-1 active:translate-y-0 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#1875FF]/5 via-[#05E0D1]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>

      {ripples.map((ripple) => (
        <span
          key={ripple.id}
          className="absolute rounded-full bg-[#05E0D1] opacity-50 animate-ping"
          style={{
            left: ripple.x,
            top: ripple.y,
            width: '20px',
            height: '20px',
            transform: 'translate(-50%, -50%)',
          }}
        />
      ))}

      <div className="relative">
        <div className="flex items-center space-x-4 mb-3">
          <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center group-hover:from-[#1875FF]/20 group-hover:to-[#05E0D1]/20 transition-all duration-300 group-hover:scale-110 group-hover:-translate-y-1 shadow-lg group-hover:shadow-xl">
            <Icon className="text-[#1875FF] group-hover:text-[#05E0D1] transition-all duration-300 group-hover:scale-110" size={28} />
          </div>
          <h3 className="text-lg font-extrabold text-gray-900 group-hover:text-[#1875FF] transition-colors duration-300 font-display">
            {title}
          </h3>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed font-medium">
          {description}
        </p>
      </div>
    </button>
  );
};

export default ActionCard;
